import { createAsyncThunk } from "@reduxjs/toolkit";
import customFetch from "../../util/config/axiosConfig";
import { Company, CompanyDetails, TokenData } from "../../types/developerTypes";
import { getDataUserId } from "../../util/manageAuth";

/**
 * 🔥 Add Company
 */
export const addCompany = createAsyncThunk<
  Company,
  { name: string; website: string },
  { rejectValue: string }
>(
  "company/addCompany",
  async (companyData, thunkAPI) => {
    try {
      const userId = getDataUserId();

      const response = await customFetch.post(`/admin/company`, {
        ...companyData,
        userId,
      });

      let payload = response.data;

      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return payload.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.error?.message ||
          error?.message ||
          "Failed to add company"
      );
    }
  }
);

/**
 * 🔥 Fetch Logged-in User Company
 * Backend filters using userId
 */
export const fetchMyCompany = createAsyncThunk<
  CompanyDetails | null,
  void,
  { rejectValue: string }
>(
  "company/fetchMyCompany",
  async (_, thunkAPI) => {
    try {
      const rawUserId = getDataUserId();
      const userId = rawUserId ? Number(rawUserId) : null;

      if (!userId) return null;

      const response = await customFetch.post(
        `/admin/company/company/search?page=0&size=10`,
        {
          userId, // 🔥 key part
        }
      );

      let payload = response.data;

      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      const results: CompanyDetails[] = payload.data?.results || [];

      return results.length > 0 ? results[0] : null;

    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch company"
      );
    }
  }
);

export const createToken = createAsyncThunk<
  string, 
  number, 
  { rejectValue: string }
>(
  "company/createToken",
  async (companyId, thunkAPI) => {
    try {
      const response = await customFetch.post(
        `/admin/token/create/${companyId}`
      );

      let payload = response.data;

      if (typeof payload?.data === "string") {
        return payload.data; 
      }

      return payload.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to generate token"
      );
    }
  }
);

export const fetchToken = createAsyncThunk<
  TokenData[], 
  number, 
  { rejectValue: string }
>(
  "company/fetchToken",
  async (companyId, thunkAPI) => {
    try {
      const response = await customFetch.get(
        `/admin/token/${companyId}`
      );

      let payload = response.data;

      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return payload.data || [];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch tokens"
      );
    }
  }
);  

export const fetchCompanyById = createAsyncThunk<
  CompanyDetails,
  number,
  { rejectValue: string }
>(
  "company/fetchCompanyById",
  async (companyId, thunkAPI) => {
    try {
      const response = await customFetch.get(`/admin/company/${companyId}`);

      let payload = response.data;

      if (typeof payload === "string") {
        payload = JSON.parse(payload);
      }

      return payload; // ← was payload.data, but response has no .data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch company"
      );
    }
  }
);


export const fetchMyCompanies = createAsyncThunk<
  CompanyDetails[],
  void,
  { rejectValue: string }
>(
  "company/fetchMyCompanies",
  async (_, thunkAPI) => {
    try {
      const rawUserId = getDataUserId();
      const userId = rawUserId ? Number(rawUserId) : null;

      if (!userId) return [];

      const response = await customFetch.post(
        `/admin/company/company/search?page=0&size=100`,
        { userId }
      );

      let payload = response.data;

      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return payload.data?.results || [];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch companies"
      );
    }
  }
);
